<template>
  <div class="bg-stw-grey-light rounded-lg px-6 pt-4 pb-6 lg:px-12 lg:pb-16 lg:pt-12 mt-10">
    <div class="flex justify-between mb-3 lg:mb-6 gap-4 flex-wrap">
      <h3 class="mb-0">
        <span v-if="locale === 'de'">Speiseplan <span v-if="todayOnly">{{ days[0].label }}</span></span>
        <span v-else>Menu</span>
      </h3>
    </div>
    <ul v-if="!todayOnly" class="mb-4 flex gap-2 md:flex-wrap overflow-scroll hide-scrollbar p-4 -mr-4 -ml-4">
      <li v-for="(day, index) in days" :key="index">
        <button @click="selectedDate = day" :class="[ selectedDate.isoDate === day.isoDate ? 'bg-green-300' : 'bg-white' ]" class="whitespace-nowrap shadow-lg lg:hover:shadow flex text-xs lg:text-base items-center font-bold gap-4 px-8 h-8 lg:h-12 rounded-full lg:hover:bg-stw-grey-dark lg:hover:text-white">
          <span v-if="isToday(day.formatDate)">{{ locale == 'de' ? 'Heute' : 'Today' }}</span>
          <span v-else>{{ day.label }}</span>
        </button>
      </li>
    </ul>
    <div class="flex flex-col md:flex-row md:flex-wrap gap-4 lg:gap-8">
      <div v-show="locationSelect" class="w-fit min-w-full md:min-w-80">
        <p class="text-md !mb-2">{{ locale == 'de' ? 'Standort' : 'Location' }}</p>
        <VueMultiselect
          :options="canteens"
          group-values="options"
          group-label="label"
          track-by="value"
          label="title"
          :allow-empty="false"
          :placeholder="locale == 'de' ? 'Standort wählen' : 'Choose location'"
          :select-label="locale == 'de' ? 'Auswählen' : 'Choose'"
          :deselect-label="locale == 'de' ? 'Enter zum abwählen' : 'Enter to remove'"
          :selected-label="locale == 'de' ? 'Ausgewählt' : 'Selected'"
          v-model="selectedObject"
        />
      </div>
      <div class="w-fit min-w-full md:min-w-80" :class="{'disabled' : availableCategories.length < 1}">
        <p class="text-md !mb-2">{{ locale == 'de' ? 'Kategorien' : 'Categories' }}</p>
        <VueMultiselect
          :options="availableCategories"
          track-by="value"
          label="name"
          :multiple="true"
          :placeholder="locale == 'de' ? 'Kategorie wählen' : 'Choose category'"
          :select-label="locale == 'de' ? 'Auswählen' : 'Choose'"
          :deselect-label="locale == 'de' ? 'Enter zum abwählen' : 'Enter to remove'"
          :selected-label="locale == 'de' ? 'Ausgewählt' : 'Selected'"
          v-model="selectedCategoryObject"
        >
          <template #selection="{ values, search, isOpen }">
            <span class="multiselect__single" v-if="values.length" v-show="!isOpen">{{ values.length }} {{ locale == 'de' ? 'Kategorie(n) ausgewählt' : 'Category(ies) selected' }}</span>
          </template>
        
          <template #option="props">
            <div class="flex justify-start items-center">
              <component class="ml-0 mr-3" :is="resolveCatIcons(props.option.icon)" /> <span class="ml-1.5">{{ props.option.name }}</span>
            </div>
          </template>

        </VueMultiselect>
      </div>
    </div>
    <div v-if="loading || translating" class="mt-10">
      <LoaderCircle class="animate-spin" size="24" stroke-width="1.5" />
      <p class="!mb-0 mt-4">
        {{ locale == 'de' ? 'Speiseplan-Daten laden' : 'Loading data' }}
      </p>
    </div>
    <div id="foodTable" class="mt-10" v-else-if="filteredCounters && filteredCounters.length">
      <table class="counterTable mt-4 w-[calc(100%_+_1.3rem)] border-spacing-2.5 border-separate -ml-2.5 -mr-2.5" v-for="(counter, index) in filteredCounters" :key="index">
        <colgroup>
          <col span="1" style="width: 55%;">
          <col class="hidden md:table-cell" span="1" style="width: 20%;">
          <col span="1" style="width: 25%;">
        </colgroup>
        <thead>
          <tr>
            <th class="font-normal px-4 py-1.5 align-top text-left rounded-sm">{{ counter.label }}</th>
            <th class="hidden md:table-cell font-normal px-4 py-1.5 align-top text-left rounded-sm">{{ locale == 'de' ? 'Kategorie' : 'Category' }}</th>
            <th class="font-normal px-4 py-1.5 align-top text-left rounded-sm">{{ locale == 'de' ? 'Preise' : 'Prices' }}</th>
          </tr>
        </thead>
        <tbody class="text-sm">
          <Meal v-for="(meal, index) in counter.meals" :meal="meal" :date="selectedDate.isoDate" :locale="props.locale" :key="index"/>
        </tbody>
      </table>
      <div class="mt-4 lg:mt-8 border-t border-stw-grey pt-4 lg:pt-8">
        <ul class="flex flex-wrap gap-4 lg:gap-6">
          <li class="flex gap-2"><Beef class="mx-0" /> {{ locale == 'de' ? 'Rind / Kalb' : 'Beaf / Veal' }}</li>
          <li class="flex gap-2"><Chicken class="mx-0" /> {{ locale == 'de' ? 'Geflügel' : 'Poultry' }}</li>
          <li class="flex gap-2"><Pork class="mx-0" /> {{ locale == 'de' ? 'Schwein' : 'Pork' }}</li>
          <li class="flex gap-2"><Fish class="mx-0" /> {{ locale == 'de' ? 'Fisch' : 'Fish' }}</li>
          <li class="flex gap-2"><Deer class="mx-0" /> {{ locale == 'de' ? 'Wild' : 'Deer' }}</li>
          <li class="flex gap-2"><Sheep class="mx-0" /> {{ locale == 'de' ? 'Lamm / Schaf' : 'Lamb / Sheep' }}</li>
          <li class="flex gap-2"><Animals class="mx-0" /> {{ locale == 'de' ? 'Artgerechte Tierhaltung' : 'Appropriate animal husbandry' }}</li>
          <li class="flex gap-2"><Vegan class="mx-0" /> {{ locale == 'de' ? 'Vegan' : 'Vegan' }}</li>
          <li class="flex gap-2"><Vegetarian class="mx-0" /> {{ locale == 'de' ? 'Vegetarisch' : 'Vegetarian' }}</li>
          <li class="flex gap-2"><Climate class="mx-0" /> {{ locale == 'de' ? 'KlimaTeller' : 'KlimaTeller' }}</li>
        </ul>
        <p class="mt-4 !mb-0 text-sm xl:text-base">{{ locale == 'de' ? 'Die im Wochenplan ausgewiesene Zusatzstoff- und Allergenkennzeichnung kann tagesaktuell abweichen. Bitte Tagesaushänge beachten. Änderungen vorbehalten.' : 'The additive and allergen labeling shown in the weekly menu may vary from day to day. Please note daily notices. Subject to change without notice.' }}</p>
      </div>
      <WeekPlan :locale="locale" :selectedlocation="selectedLocation.toString()" :canteens="canteens" :locationname="selectedLocationName" :locationurl="`${locationurl}`" />
    </div>
    <div v-else class="mt-10 p-6 bg-red-400 rounded-lg flex gap-2 text-xl items-center">
      <Frown size="24" stroke-width="1.5" />
      <p class="!mb-0">{{ locale == 'de' ? 'Leider keine Speiseplan-Daten gefunden' : 'No data available' }}</p>
    </div>
  </div>
</template>

<script setup>
  import { generateDayObjects, getMeals, foodCatNames, isToday, findOptionByValue } from "../../lib/menuHelper"
  import { flatten } from "../../lib/helper"
  import { computed, ref, onMounted, watch } from 'vue'
  import Meal from "../elements/Meal.vue"
  import WeekPlan from "../elements/WeekPlan.vue"
  import { Frown, LoaderCircle }  from 'lucide-vue-next'
  import VueMultiselect from 'vue-multiselect'
  import Beef from "../foodicons/Beef.vue"
  import Chicken from "../foodicons/Chicken.vue"
  import Vegan from "../foodicons/Vegan.vue"
  import Vegetarian from "../foodicons/Vegetarian.vue"
  import Fish from "../foodicons/Fish.vue"
  import Pork from "../foodicons/Pork.vue"
  import Animals from "../foodicons/Animals.vue"
  import Deer from "../foodicons/Deer.vue"
  import Sheep from "../foodicons/Sheep.vue"
  import Climate from "../foodicons/Climate.vue"

  const props = defineProps({
    locale: {
      type: String,
      required: true,
    },
    locationid: {
      type: String,
      required: false
    },
    locationname: {
      type: String,
      required: false
    },
    locationurl: {
      type: String,
      required: true
    },
    locationSelect: {
      type: Boolean,
      required: false,
      default: true
    },
    canteens: {
      type: Array,
      required: true
    },
    canteen: {
      type: Object
    },
    todayOnly: {
      type: Boolean,
      default: false
    }
  });

  const locationChanged = ref(false)

  const baseUrl = import.meta.env.PUBLIC_KIRBY_BASE_URL

  const selectedLocation = computed(() => {
    if (typeof selectedObject.value === 'object') {
      return selectedObject.value.value
    } else {
      return selectedObject.value
    }
  })

  const selectedCategory = computed(() => {
    if (selectedCategoryObject.value) {
      return selectedCategoryObject.value.map(r => r.value)
    } else {
      return null
    }
  })

  const selectedLocationURL = computed(() => {
    if (locationChanged.value) {
      return findOptionByValue(props.canteens, selectedLocation.value)
    } else {
      return props.locationurl
    }
  })

  function replaceTextValues(data, replacements) {
    let index = 0;

    function traverseAndReplace(obj) {
        if (Array.isArray(obj)) {
            obj.forEach((item, i) => {
                obj[i] = traverseAndReplace(item);
            });
        } else if (typeof obj === 'object' && obj !== null) {
            for (let key in obj) {
                if (typeof obj[key] === 'string') {
                    obj[key] = replacements[index++];
                } else {
                    obj[key] = traverseAndReplace(obj[key]);
                }
            }
        }
        return obj;
    }

    return traverseAndReplace(data);
  }

  function extractTextValues(data) {
    let textValues = [];

    function traverse(obj) {
        if (Array.isArray(obj)) {
            obj.forEach(item => traverse(item));
        } else if (typeof obj === 'object' && obj !== null) {
            for (let key in obj) {
                if (typeof obj[key] === 'string') {
                    textValues.push(obj[key]);
                } else {
                    traverse(obj[key]);
                }
            }
        }
    }

    traverse(data);
    return textValues;
  }

  async function translate() {
    if (textValues.value) {
      const translatedTable = await fetch(`${baseUrl}/api/translatefood`, {
        method: 'POST',
        header: {
          'Content-Type':'application/json',
          'X-Language':`${props.locale}`
        },
        body: JSON.stringify({
          text: textValues.value
        })
      })
      const data = await translatedTable.json()
      translatedTextValues.value = data.result.map(rs => rs.text)
      replaceTextValues(counters.value, translatedTextValues.value)
      translating.value = false
    }
  }

  const loading = ref(true)
  const translating = ref(true)
  const days = generateDayObjects(props.locale)
  const selectedCategoryObject = ref(null)
  const selectedObject = ref(props.canteen)
  const selectedDate = ref(days[0])
  const counters = ref(null)
  const textValues = ref()
  const translatedTextValues = ref()

  async function fetchMeals() {
    counters.value = await getMeals(selectedLocation.value, selectedDate.value.formatDate, props.locale, selectedLocationURL.value)
    if (props.locale === 'en' && counters.value && counters.value.length) {
      textValues.value = extractTextValues(counters.value)
      await translate()
    } else {
      translating.value = false
    }
    loading.value = false
  }

  onMounted(() => {
    fetchMeals()
  });

  watch(() => selectedDate.value, (newDate) => {
    // Fetch meals whenever selectedDate changes
    translating.value = true
    loading.value = true
    fetchMeals()
  });

  watch(() => selectedLocation.value, (newLocation) => {
    // Fetch meals whenever selectedLocation changes
    locationChanged.value = true
    selectedCategoryObject.value = null
    translating.value = true
    loading.value = true
    fetchMeals()
  });

  const selectedLocationName = computed(() => {
    for (const category of props.canteens) {
        for (const option of category.options) {
            if (option.value === selectedLocation.value) {
                return option.title;
            }
        }
    }
  })

  // Define computed properties outside of onMounted
  const availableCategories = computed(() => {
    if (!counters.value) return []; // Guard against null values
    const cats = counters.value.map(counter => {
          return counter.meals.map(meal => meal.categories)
        })
    return [...new Set(flatten(cats).sort((a,b) => a - b))].map(cat => {
      return {
        value: cat.toString(),
        name: foodCatNames(cat, props.locale),
        icon: cat
      }
    })
  })

  const filteredCounters = computed(() => {
    if (!counters.value) return []; // Guard against null values
    if (selectedCategory.value === null || selectedCategory.value === 'null') {
      return counters.value;
    }
    return counters.value.map(counter => ({
      ...counter,
      meals: counter.meals.filter(meal => {
        if (selectedCategory.value.length) {
          return selectedCategory.value.some(r=> meal.categories.includes(r));
        } else {
          return true;
        }
      })
    })).filter(counter => counter.meals.length > 0);
  });

  const resolveCatIcons = (catNo) => {
    switch(catNo){
        case "R":
          return Beef;
          break;
        case "G":
          return Chicken;
          break;
        case "S":
          return Pork;
          break;
        case "F":
          return Fish;
          break;
        case "VT":
          return Vegetarian;
          break;
        case "VG":
          return Vegan;
          break;
        case "W":
          return Deer;
          break;
        case "AT":
          return Animals;
          break;
        case "KT":
          return Climate;
          break;
        default:
          return Beef;
    }
  }
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
.disabled {
  display: none;
  filter: grayscale(1);
  opacity: .25;
  pointer-events: none;
}

.character-option-icon {
  margin: 0 6px 0 0;
  height: 22px;
}

.character-label-icon {
  margin: 0 6px 0 0;
  height: 26px;
}
.filterSelect {
  --ms-tag-font-weight: 700;
  --ms-tag-bg: #263238;
  --ms-tag-py: 4px;
  --ms-tag-radius: 20px;
  --ms-px: 2rem;
  --ms-tag-my: 0;
  --ms-tag-mx: 0;
  --ms-option-bg-selected-pointed: #86efac;
  --ms-option-bg-selected: #86efac;
  --ms-option-color-selected-pointed: #263238;
  --ms-option-color-selected: #263238;
  --ms-border-width: 1.5px;
  --ms-border-color: #263238;
  --ms-caret-color: #263238;
  --ms-clear-color: #263238;

  .multiselect-wrapper {
    @apply lg:h-12;
  }

  .multiselect-caret, .multiselect-clear-icon {
    @apply w-4
  }

  .multiselect-tags {
    @apply flex py-2 px-4 gap-2
  }
  
  .multiselect-dropdown {
    @apply -bottom-2 rounded-lg shadow-lg
  }
}
.multiselect-options {
  @apply flex flex-wrap;
  .multiselect-option:not(:last-of-type) {
    @apply border-b px-3 py-2 font-normal
  }
}

div.multiselect {
  @apply bg-white;
  
  &.multiselect--active {
    @apply bg-transparent;
  }

  span.multiselect__placeholder {
    @apply text-black h-12 px-6 py-0 flex items-center text-base mb-0;
  }

  .multiselect__option--highlight {
    @apply bg-stw-food-light after:bg-stw-food-light 
  }

  .multiselect__option {
    @apply text-black h-10 flex items-center
  }

  .multiselect__option--disabled {
    @apply bg-neutral-200 text-neutral-700 font-bold #{!important};
  }

  @apply border-0 p-0 m-0 h-12 rounded-full shadow-md;

  .multiselect__content-wrapper {
    @apply top-full mt-2 rounded-lg shadow-lg border-neutral-300 border-[1.5px] border-t-[1.5px] border-b-[1.5px];

    .multiselect__content {
      @apply rounded-lg;
    }

    .multiselect__element:not(:last-of-type) {
      @apply border-b border-neutral-300 #{!important}
    }
  }

  .multiselect__select {
    @apply h-12 flex items-center m-0 p-0 justify-center before:top-0 before:mt-0 before:border-t-black z-10 right-4;
  }

  .multiselect__option--highlight {
    @apply bg-green-300 after:bg-green-300;
  }

  .multiselect__option--selected {
    @apply bg-green-300 after:bg-green-300;
  }

  .multiselect__tags {
    @apply border-0 p-0 m-0 h-12 rounded-full flex bg-transparent;

    input.multiselect__input {
      @apply border-0 py-0 px-6 m-0 h-12 rounded-full flex items-center;
    }

    .multiselect__single {
      @apply border-0 py-0 px-6 m-0 h-12 rounded-full flex items-center;
    }
  }
}
</style>